// HOOKS
import useScreenSize from "../../../../hooks/useScreenSize";

// REDUX
import { useDispatch } from "react-redux";
import { updateCryptoStep } from "../../../../store/slices/cryptoSlice";

// COMPONENTS
import { ModalBody, ModalHeader, usePagination } from "@nextui-org/react";
import { ButtonsFooter } from "../ButtonsFooter/ButtonsFooter";
import { Stepper } from "../../../Stepper/Stepper";
import Alert from "../../../Alert/Alert";

// CONSTANTS
import { MOBILE_OPERATE_WIDTH } from "../../../OperateMEP/constants";

// STYLES
import styles from "./DetailsModal.module.scss";

// ASSETS
import CryptoImg from "../../../../assets/images/illustrations/bitcoin-icon.svg?react";

interface Props {
  onClose: () => void;
  companyName: string;
  isSingleAccount: boolean;
  singleAccountHasJS?: boolean | null;
}
const DetailsModal = ({
  companyName,
  isSingleAccount,
  onClose,
  singleAccountHasJS = null,
}: Props) => {
  const {
    header,
    flexContainer,
    scrollContent,
    cryptoImg,
    headerContainer,
    body,
    textContent,
    alertInfo,
  } = styles;
  const { isMobile } = useScreenSize(MOBILE_OPERATE_WIDTH);
  const dispatch = useDispatch();
  const pagination = usePagination({
    total: 3,
    showControls: false,
  });

  const { activePage, onNext } = pagination;

  const getPaginationData = () => {
    switch (activePage) {
      case 1:
        return {
          title: `¿Qué es ${companyName}?`,
          body: `${companyName} SA es el Proveedor de Servicios de Activos Virtuales de Allaria SA registrado ante la CNV, matrícula N° 132.`,
        };
      case 2:
        return {
          title: isMobile
            ? `¿Qué es ${companyName}?`
            : "¿Cómo abro una cuenta?",
          body: `Con tu cuenta comitente en Allaria SA, creá tu cuenta en ${companyName} sin ningún costo aceptando nuestros Términos y Condiciones.`,
        };
      case 3:
        return {
          title: isMobile ? `¿Qué es ${companyName}?` : "¿Cómo funciona?",
          body: "Operá activos digitales utilizando el saldo disponible de tu cuenta comitente de Allaria SA.",
        };
    }
  };

  const getPrimaryButtonProps = () => {
    return {
      label: "Continuar",
      action: () => {
        if (activePage === 3) {
          dispatch(
            updateCryptoStep(
              isSingleAccount && singleAccountHasJS
                ? "JOINT_SIGNATURE"
                : "CONFIRM"
            )
          );
        } else {
          onNext();
        }
      },
    };
  };

  const getSecondaryButtonProps = () => {
    return {
      label: isSingleAccount ? "Cerrar" : "Volver",
      action: () => {
        if (isSingleAccount) {
          onClose();
        } else {
          dispatch(updateCryptoStep("ACCOUNTS"));
        }
      },
    };
  };

  return (
    <>
      <ModalHeader
        className={` ${headerContainer} ${!isMobile ? "pb-[1.5rem]" : "pb-[0.75rem]"}`}
      >
        {isMobile && <CryptoImg />}
        <span className={header}>{getPaginationData()?.title}</span>
        {!isMobile && <Stepper pagination={pagination} />}
      </ModalHeader>

      <ModalBody
        className={`${flexContainer} ${isMobile && "py-[0px] px-[1rem]"}`}
      >
        <div className={scrollContent}>
          <div className={textContent}>
            <p className={body}>{getPaginationData()?.body}</p>
            {activePage === 3 &&
              (isMobile ? (
                <p className={body}>
                  Transferencias bancarias días hábiles entre las 10 y las 17hs.
                </p>
              ) : (
                <Alert className={alertInfo} variant={"INFO"}>
                  Transferencias bancarias días hábiles entre las 10 y las 17hs.
                </Alert>
              ))}
          </div>
          {!isMobile && <CryptoImg className={cryptoImg} />}
        </div>

        {isMobile && (
          <Stepper
            pagination={pagination}
            classNames={{ paginationList: "pb-[2.12rem]" }}
          />
        )}
      </ModalBody>

      <ButtonsFooter
        isMobile={isMobile}
        primaryButton={{
          text: getPrimaryButtonProps().label,
          handleClick: getPrimaryButtonProps().action,
          isDisabled: false,
        }}
        secondaryButton={{
          text: getSecondaryButtonProps().label,
          handleClick: getSecondaryButtonProps().action,
          isDisabled: false,
        }}
      />
    </>
  );
};

export default DetailsModal;
